const API_URL = 'https://shabars.kindlesurveymanager.kindle.amazon.dev/surveymanager';

/**
 * This function parses the browser's cookies to find and extract the value
 * of the 'at-main' authentication token. The token is used for API authorization.
 *
 * Example cookie format:
 * document.cookie might look like:
 * "at-main=abc123xyz; Path=/; Domain=.kindlesurveymanager.kindle.amazon.dev; Secure; HttpOnly"
 *
 * @returns {string|null} The value of at-main token if found, null otherwise
 *
 * @example
 * // Given cookie string:
 * // "session=456def; at-main=SAMPLE_TOKEN; other=xyz"
 * const token = getAtMainCookie();
 * // Returns: "SAMPLE_TOKEN_789"
 *
 * // If no at-main cookie exists:
 * const token = getAtMainCookie();
 * // Returns: null
 */
 const getAtMainCookie = () => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
        cookie = cookie.trim();
        if (cookie.startsWith('at-main=')) {
            return cookie.substring('at-main='.length);
        }
    }
    return null;
};

/**
 * Fetches survey URLs from the survey manager API
 *
 * Makes an authenticated GET request to the survey manager endpoint to retrieve
 * available surveys. Uses the at-main token from cookies for authorization.
 * 
 * @returns Array of survey objects
 * @throws {Error} If the network request fails or returns non-200 status
 * */
export const fetchSurveyUrls = async () => {
    const atMain = getAtMainCookie();

    try {
        const response = await fetch(API_URL, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Authorization': atMain
            }
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const text = await response.text();
        if (!text) {
            console.log('Empty response received');
            return [];
        }

        try {
            const data = JSON.parse(text);

            if (!data) {
                console.log('Null or undefined response');
                return [];
            }

            if (Array.isArray(data)) {
                return data;
            }

            if (data.surveys && Array.isArray(data.surveys)) {
                return data.surveys;
            }

            console.log('Unexpected response format:', data);
            return [];

        } catch (parseError) {
            console.error('JSON parsing error:', parseError);
            return [];
        }

    } catch (error) {
        console.error('Error fetching surveys:', error);

        if (error instanceof TypeError) {
            console.error('Network error:', error);
            throw new Error('Network error occurred while fetching surveys');
        }

        if (error.name === 'AbortError') {
            console.error('Request aborted:', error);
            throw new Error('Request to fetch surveys was aborted');
        }

        throw error;
    }
};